import { IBadge } from '@faire/web-api--source/indigofair/badges/IBadge';
import { IBadgeList } from '@faire/web-api--source/indigofair/badges/IBadgeList';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { ILayoutElementData } from '@faire/web-api--source/indigofair/data/ILayoutElementData';
import { ILeanProductTile } from '@faire/web-api--source/indigofair/data/ILeanProductTile';
import { IProductFirstCatNavRequest } from '@faire/web-api--source/indigofair/data/IProductFirstCatNavRequest';
import { IRetailerHomepageTemplate } from '@faire/web-api--source/indigofair/data/IRetailerHomepageTemplate';
import { ISearchMakersWithFiltersRequest } from '@faire/web-api--source/indigofair/data/ISearchMakersWithFiltersRequest';
import { IUserMilestone } from '@faire/web-api--source/indigofair/data/IUserMilestone';

export namespace ILayoutElement {
  export namespace IOpenModal {
    /**
     * Return an instance of IOpenModal with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IOpenModal>): IOpenModal =>
      ({
        ...(partial ?? {}),
      }) as IOpenModal;
  }

  /**
   * The tags are out of order. All tag numbers must be unique. When adding fields, also update the field below and the tag number.
   * See https://www.notion.so/faire/Protocol-Buffers-Protos-ba527d2f70c34ab983b8d0417c6a56ac#6a792ceb9d37498795a117e712b9e7e7 for details.
   * The last tag added was `impressionPathOverride`. The next tag number is 61.
   */
  export interface IOpenModal {
    image_url: string | undefined;
    title: string | undefined;
    subtitle: string | undefined;
    cta_text: string | undefined;
    cta_link: string | undefined;
  }

  export namespace IToggleButton {
    /**
     * Return an instance of IToggleButton with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IToggleButton>): IToggleButton =>
      ({
        ...(partial ?? {}),
      }) as IToggleButton;
  }

  /**
   * Toggle Button supports 2 ctas, one for when toggle state is true, other for when toggle state is false. These ctas
   * can be exact same depending on design. E.g. "Learn more about xxx" and "Collapse xxx".
   */
  export interface IToggleButton {
    /**
     * Copy to show when the state of toggle button is open. E.g. "- Show less"
     */
    open_cta_text: string | undefined;
    /**
     * Copy to show when the state of toggle button is closed. E.g. "+ Show more"
     */
    closed_cta_text: string | undefined;
    default_state: boolean | undefined;
  }

  /**
   * https://www.notion.so/faire/Logged-in-homepage-fbd0e84c89ca46e0b69c04aeff33bd01
   */
  export enum Type {
    /**
     * This should not be used
     */
    TYPE_UNKNOWN = 'TYPE_UNKNOWN',
    /**
     * https://www.notion.so/faire/Logged-in-homepage-fbd0e84c89ca46e0b69c04aeff33bd01#fdbdaae8355e48efb23307335c0d5e23
     * These are the main value propositions. The element is too complex for us to return specifications for it.
     * The frontend is supposed to know how to render this element.
     */
    MAIN_VALUE_PROPS = 'MAIN_VALUE_PROPS',
    /**
     * https://www.notion.so/faire/Logged-in-homepage-fbd0e84c89ca46e0b69c04aeff33bd01#f2024139b77c4275b0e2f29ab6184987
     * This is one of the simplest elements, it's just a carousel of small brand tiles.
     */
    SMALL_MAKER_TILE_CAROUSEL = 'SMALL_MAKER_TILE_CAROUSEL',
    /**
     * https://www.notion.so/faire/Logged-in-homepage-fbd0e84c89ca46e0b69c04aeff33bd01#bed5d35bf50c440ca788c04ac72df98d
     * This should be one of the more versatile elements.
     */
    IN_PAGE_IMAGE = 'IN_PAGE_IMAGE',
    /**
     * https://www.figma.com/file/YKwphR5HRRn7DyLRipc8uN/FSM-'22-Retailer-Experience?node-id=804%3A12364
     */
    IN_PAGE_IMAGE_SECONDARY = 'IN_PAGE_IMAGE_SECONDARY',
    /**
     * https://www.notion.so/faire/Logged-in-homepage-fbd0e84c89ca46e0b69c04aeff33bd01#030ccfe44f1f414c9a2e9ed4c805f8d0
     * This element looks very similar to the brand carousel but contains flash sales instead.
     */
    SMALL_FLASH_SALE_CAROUSEL = 'SMALL_FLASH_SALE_CAROUSEL',
    /**
     * https://www.figma.com/file/8Ttp9nIOACEpoh1kPhfSiW/Treasure-Hunt-v1?node-id=78%3A8926
     * Large tile with information for a single maker displayed in a carousel format.
     * Currently used on mobile only.
     */
    LARGE_MAKER_TILE = 'LARGE_MAKER_TILE',
    /**
     * https://www.notion.so/faire/R2B-entry-points-in-app-10db50215f14429d89f4883db9ab7324
     * Used for R2B entry points
     * This type is NOT supported by clients and is for INTERNAL usage ONLY.
     */
    R2B_ENTRY_POINT = 'R2B_ENTRY_POINT',
    /**
     * https://www.notion.so/faire/Apparel-C1-Homepage-9e8a310df46d40a9bb54d7124ad3b4cf
     * https://www.figma.com/file/vRIxmI7iQ25PCxiuX4uJCO/Apparel-C1-Homepage?node-id=235%3A3653
     * Element for displaying a products carousel that has tiles similar/same to products-first-cat-nav
     */
    PRODUCT_TILE_CAROUSEL = 'PRODUCT_TILE_CAROUSEL',
    /**
     * https://www.notion.so/faire/Apparel-C1-Homepage-9e8a310df46d40a9bb54d7124ad3b4cf
     * https://www.notion.so/faire/Treatment-bucket-details-881964f845f94ee5bdea9293e4c73857#c9c93c9a37d94d82b8d88eda0a913255
     * Carousel of categories with portrait images instead of landscape
     * Also used at
     * https://www.notion.so/faire/Redesign-filtering-experience-Browse-bfea4633995b4d8d9fdb22708f0d14ae#3cef2d8c709e4eafbebdf7ebbdf5d3a8
     * https://www.figma.com/file/oV3rDne164xbSujsZlDutA/apparel-visual-navigation?node-id=828%3A60190
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=628%3A56250
     */
    TALL_CATEGORY_TILE = 'TALL_CATEGORY_TILE',
    /**
     * https://www.notion.so/faire/Apparel-C1-Homepage-9e8a310df46d40a9bb54d7124ad3b4cf
     * https://www.notion.so/faire/Treatment-bucket-details-881964f845f94ee5bdea9293e4c73857#c9c93c9a37d94d82b8d88eda0a913255
     * Carousel of categories with smaller tiles
     */
    SMALL_CATEGORY_TILE = 'SMALL_CATEGORY_TILE',
    /**
     * https://www.figma.com/file/A71waWpOl5FioDHdiBIzF1/Contextual-education-in-mobile-app?node-id=174%3A959
     * Testimonial tile currently for mobile app home feed only
     * Matches TestimonialTile.
     * @deprecated
     */
    TESTIMONIAL = 'TESTIMONIAL',
    /**
     * https://www.notion.so/faire/Redesign-filtering-experience-Browse-bfea4633995b4d8d9fdb22708f0d14ae#ce5849da02004d3294c088d01471574b
     * https://www.figma.com/file/oV3rDne164xbSujsZlDutA/apparel-visual-navigation?node-id=828%3A60190
     * Carousel of categories with circular images
     */
    CIRCULAR_CATEGORY_TILE = 'CIRCULAR_CATEGORY_TILE',
    /**
     * https://www.figma.com/file/A71waWpOl5FioDHdiBIzF1/Contextual-education-in-mobile-app?node-id=203%3A1165
     * Dismissable banner on top of category and search pages
     */
    DISMISSABLE_TOP_BANNER = 'DISMISSABLE_TOP_BANNER',
    /**
     * https://whimsical.com/new-retailer-logged-in-homepage-QFEgtWyoPr7E1gv8F3CUUg
     * Homepage app download banner for FE to populate the content
     */
    HOMEPAGE_APP_DOWNLOAD_BANNER = 'HOMEPAGE_APP_DOWNLOAD_BANNER',
    /**
     * https://www.figma.com/file/BvAqRhjm9Dms9LvErvwzEY/New-retailer-logged-in-homepage?node-id=696%3A10651
     * A plain text banner highlighting a promotional discount with an expiration date/time.
     * This type is NOT supported by clients and is for INTERNAL usage ONLY.
     */
    PROMOTIONAL_DISCOUNT_BANNER = 'PROMOTIONAL_DISCOUNT_BANNER',
    /**
     * https://www.figma.com/file/BvAqRhjm9Dms9LvErvwzEY/New-retailer-logged-in-homepage?node-id=779%3A13997
     * Homepage banner with suggested searches and images
     */
    HOMEPAGE_SEARCH_QUERIES_BANNER = 'HOMEPAGE_SEARCH_QUERIES_BANNER',
    /**
     * A grid of product tiles list for cat-nav or browse page. This type is NOT supported by client and is
     * for INTERNAL usage ONLY.
     */
    PRODUCT_TILE_GRID = 'PRODUCT_TILE_GRID',
    /**
     * https://www.figma.com/file/hfScsfxitzaGgIpMlCAYI5/MPP-Marketplace?node-id=1400%3A29703
     * Category homepage header banner for mid price point plus marketplace
     */
    MID_PRICE_POINT_PLUS_CATEGORY_HOME_PAGE_HEADER_BANNER = 'MID_PRICE_POINT_PLUS_CATEGORY_HOME_PAGE_HEADER_BANNER',
    /**
     * https://www.figma.com/file/hfScsfxitzaGgIpMlCAYI5/MPP-Marketplace?node-id=463%3A6198
     * Banner shows mid price point plus products to shop
     */
    SHOP_MID_PRICE_POINT_PLUS_PRODUCTS_BANNER = 'SHOP_MID_PRICE_POINT_PLUS_PRODUCTS_BANNER',
    /**
     * https://www.figma.com/file/hfScsfxitzaGgIpMlCAYI5/MPP-Marketplace?node-id=1686%3A27609
     * Mid price point plus marketplace page title on c3 category page and search page,
     * it shows up when MPP+ filter is applied.
     */
    MID_PRICE_POINT_PLUS_PAGE_TITLE = 'MID_PRICE_POINT_PLUS_PAGE_TITLE',
    /**
     * https://www.figma.com/file/byIl3UHFCP7cdPTN3zJ8Mw/Evergreen-Preorder-Visibility?node-id=1313%3A13747
     */
    PRODUCT_IMAGE_CAROUSEL = 'PRODUCT_IMAGE_CAROUSEL',
    /**
     * https://www.figma.com/file/byIl3UHFCP7cdPTN3zJ8Mw/Evergreen-Preorder-Visibility?node-id=1278%3A14961
     */
    LARGE_PRODUCT_GRID = 'LARGE_PRODUCT_GRID',
    /**
     * https://www.notion.so/faire/BE-Scoping-f3a2e3857db4473b9029c7dd613d16c2#ada779a95c3d4e2a8b935038c5d69441
     */
    HIGHLIGHTED_PRODUCTS_CAROUSEL = 'HIGHLIGHTED_PRODUCTS_CAROUSEL',
    /**
     * https://www.figma.com/file/hfScsfxitzaGgIpMlCAYI5/MPP-Marketplace?node-id=3129%3A37122
     * A simple banner with only one mainText and one descriptionText
     * The browse pod took over this text banner and modified it to this format.
     * https://www.figma.com/file/acGGYdQ1JMiFe0KY1ZLgDG/Special-Occasions?type=design&node-id=5957-103188&mode=dev
     */
    SIMPLE_TEXT_BANNER = 'SIMPLE_TEXT_BANNER',
    /**
     * Banner under `European Brands` category to promote European brands for retailers; designs here:
     * https://www.figma.com/file/HGw9T0xeGz925mEOaL9B7p/European-Brand-Discovery-for-US-retailers?node-id=572%3A62825
     */
    SHOP_EUROPE_EDUCATIONAL_BANNER = 'SHOP_EUROPE_EDUCATIONAL_BANNER',
    /**
     * Mobile homepage feed for reorders; designs here:
     * https://www.figma.com/file/bzCPM5soj0OkW9Xp6ARdfg/My-Brands?node-id=2657%3A75857
     */
    REORDER_HOMEFEED_CAROUSEL = 'REORDER_HOMEFEED_CAROUSEL',
    /**
     * Showroom page core value props with C2;
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=628%3A56250
     */
    SHOWROOM_CORE_VALUE_PROPS_WITH_C2 = 'SHOWROOM_CORE_VALUE_PROPS_WITH_C2',
    /**
     * Showroom page illustrate vetting criteria banner;
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=628%3A56250
     */
    SHOWROOM_ILLUSTRATE_VETTING_CRITERIA_BANNER = 'SHOWROOM_ILLUSTRATE_VETTING_CRITERIA_BANNER',
    /**
     * Showroom page editorialized categories;
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=628%3A56250
     */
    SHOWROOM_EDITORIALIZED_CATEGORIES = 'SHOWROOM_EDITORIALIZED_CATEGORIES',
    /**
     * Showroom page featured brands;
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=628%3A56250
     */
    SHOWROOM_FEATURED_BRANDS = 'SHOWROOM_FEATURED_BRANDS',
    /**
     * Showroom page highlight brand banner;
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=628%3A56250
     */
    SHOWROOM_HIGHLIGHT_BRAND_BANNER = 'SHOWROOM_HIGHLIGHT_BRAND_BANNER',
    /**
     * Showroom page vetting criteria;
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=628%3A56250
     */
    SHOWROOM_VETTING_CRITERIA = 'SHOWROOM_VETTING_CRITERIA',
    /**
     * A showroom page title banner contains showroom values on mobile app
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=309%3A39825
     */
    SHOWROOM_VALUES_TITLE_BANNER = 'SHOWROOM_VALUES_TITLE_BANNER',
    /**
     * A PDP/brand page inline banner contains showroom values on mobile app
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=309%3A38096
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=309%3A40288
     */
    SHOWROOM_VALUES_PROPS_INLINE_BANNER = 'SHOWROOM_VALUES_PROPS_INLINE_BANNER',
    /**
     * Showroom returning user page, featured showroom products carousel
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=1826%3A81915
     */
    SHOWROOM_FEATURED_PRODUCTS_CAROUSEL = 'SHOWROOM_FEATURED_PRODUCTS_CAROUSEL',
    /**
     * Showroom returning user page, trending showroom products carousel
     * Designs here:
     * https://www.figma.com/file/nMAgBhL6oxN8dZr6zJxSAS/Showroom-v2?node-id=2724%3A93352
     */
    SHOWROOM_TRENDING_PRODUCTS_CAROUSEL = 'SHOWROOM_TRENDING_PRODUCTS_CAROUSEL',
    /**
     * Represents a collection of LeanProductWithBrandTile, i.e. a product tile with a brand section on the bottom.
     * https://www.figma.com/file/AeF21BMh8kVZAT1n58GpfQ/FFWv2-%E2%80%93--Retailer-Handoff?node-id=2510%3A36232
     * Note that some clients might prefer to render this as a LeanProductTile to avoid clutter.
     */
    LEAN_PRODUCT_WITH_BRAND_TILE_CAROUSEL = 'LEAN_PRODUCT_WITH_BRAND_TILE_CAROUSEL',
    /**
     * Represents a message carousel to show lifecycle banners for mobile homepage.
     * https://www.figma.com/file/psY2yAQaFYI3IS9YmjpfjQ/App-Home-2.0?node-id=2216%3A117384
     * @deprecated
     */
    HOME_PAGE_MESSAGE_CAROUSEL = 'HOME_PAGE_MESSAGE_CAROUSEL',
    /**
     * Used on explore page to render related searches query pills.
     * https://www.figma.com/file/Qz0NDlHgvUMIckhIjNXE30/Explore-Pages-Design-(v2)?node-id=563%3A41256
     */
    SEARCH_QUERIES_CAROUSEL = 'SEARCH_QUERIES_CAROUSEL',
    /**
     * Filter pills for a corresponding search keyword
     * https://www.figma.com/file/Qz0NDlHgvUMIckhIjNXE30/Explore-Pages-Design-(v2)?node-id=576%3A56728
     */
    SEARCH_FILTER_PILLS = 'SEARCH_FILTER_PILLS',
    /**
     * Used to render faire gift guide section.
     * https://www.figma.com/file/Qz0NDlHgvUMIckhIjNXE30/Explore-Pages-Design-(v2)?node-id=563%3A47888
     */
    GIFT_GUIDE_TILE = 'GIFT_GUIDE_TILE',
    /**
     * Used to render a carousel of testimonials.
     * https://www.figma.com/file/Qz0NDlHgvUMIckhIjNXE30/Explore-Pages-Design-(v2)?node-id=563%3A47472
     */
    TESTIMONIAL_CAROUSEL = 'TESTIMONIAL_CAROUSEL',
    /**
     * Represents a welcome element.
     * https://www.figma.com/file/psY2yAQaFYI3IS9YmjpfjQ/App-Home-2.x?node-id=549%3A25679
     */
    MOBILE_WELCOME_CATEGORIES = 'MOBILE_WELCOME_CATEGORIES',
    /**
     * Used for rendering the Insider upsell carousel on homepage as part of Insider Universal Framework
     * https://www.figma.com/file/HfEQVACpqhZIoLXyt0NAqF/Universal-Insider-Framework?node-id=6631%3A199177
     * This type is NOT supported by clients and is for INTERNAL usage ONLY.
     */
    INSIDER_HOMEPAGE_UPSELL_CAROUSEL = 'INSIDER_HOMEPAGE_UPSELL_CAROUSEL',
    /**
     * Used to show retailer brand insights carousel on the homepage.
     * https://www.figma.com/file/p3jLRuYGsFkhdvRhSPQfK1/Reorder-updates-hub-%5BReorder%5D?node-id=2085%3A143521
     */
    RETAILER_BRAND_INSIGHTS_CAROUSEL = 'RETAILER_BRAND_INSIGHTS_CAROUSEL',
    /**
     * Used to show the retailer their recent searched terms on the homepage.
     * https://www.figma.com/file/EpPi2pK16BV0gJP4WD60is/Saved-searches-%26-Jump-back-In?node-id=2207%3A76837
     */
    RECENT_SEARCHES_CAROUSEL = 'RECENT_SEARCHES_CAROUSEL',
    /**
     * This is a carousel type that will show brands that a retailer can sample.
     * Part of the SAMPLES product area
     * https://www.figma.com/file/Etra91XngZRaM21UG4lX5L/Food-%26-Beverage-Samples?node-id=4635%3A95198&t=jfuVEXdF4qiy1zOz-0
     */
    SAMPLE_REQUEST_CAROUSEL = 'SAMPLE_REQUEST_CAROUSEL',
    /**
     * Used to show products that a retailer sampled.
     * Part of the SAMPLES product area
     * https://www.figma.com/file/Etra91XngZRaM21UG4lX5L/Food-%26-Beverage-Samples?node-id=5297%3A117161&t=Ik9UVigAz911gQ6H-0
     */
    BUY_FROM_SAMPLES_CAROUSEL = 'BUY_FROM_SAMPLES_CAROUSEL',
    /**
     * A vertically expandable product grid for the shopping loops project.
     * https://www.figma.com/file/LSlw27H95cN8QgKmAJpke5/Shopping-loops-on-PDP?node-id=3684%3A133066&t=4Wl9ltHaUYTUbvjR-0
     */
    VERTICALLY_EXPANDABLE_PRODUCT_GRID = 'VERTICALLY_EXPANDABLE_PRODUCT_GRID',
    /**
     * This element is a no op placeholder. Clients should not render anything when they encounter this
     * type. However, they should still respect the layoutElementDataAssignSettingsOnClientView field. This allows us to
     * do correct bucketing on experiments that remove or add elements to a particular layout.
     */
    NO_OP_LAYOUT_ELEMENT = 'NO_OP_LAYOUT_ELEMENT',
    /**
     * A Carousel containing grid tiles, designed for product based collections
     * https://www.figma.com/file/oUVZghhJIL3cAhJO6VC4vJ/Discovering-Fresh-Apparel-%2F-Collection-Launch-drops?node-id=552%3A94727&t=W0HZi5Z7RbR5OQFe-0
     * @deprecated
     */
    GRID_TILE_CAROUSEL = 'GRID_TILE_CAROUSEL',
    /**
     * Used to display the most popular brands in a region on logged out suppliers pages
     * https://www.notion.so/faire/New-page-type-Brand-results-pages-dbef9fff47054ae0bf45aed259ffe0e7?pvs=4#2c833bc37d6b4fdf9dee6a913df891ac
     */
    MOST_POPULAR_BRANDS = 'MOST_POPULAR_BRANDS',
    /**
     * use to display mobile pills on mobile web
     */
    MOBILE_WEB_PILLS = 'MOBILE_WEB_PILLS',
    /**
     * use to display category banner
     */
    COMMUNITY_CATEGORY_BANNER = 'COMMUNITY_CATEGORY_BANNER',
    /**
     * https://www.figma.com/file/ThPbgnU0IA0MbDDVpFsxPf/Warehouse-Sale?node-id=4526-10676&t=exoKgLV3BVpS8X3Q-4
     * This element looks very similar to the SMALL_FLASH_SALE_CAROUSEL, tweaked for seasonal warehouse sale
     */
    WAREHOUSE_SALE_CAROUSEL = 'WAREHOUSE_SALE_CAROUSEL',
    /**
     * https://www.figma.com/file/mVJQw4r8Oj2RMIDynzcigE/%E2%98%80%EF%B8%8F-Faire-Summer-Market-2023?type=design&node-id=3095-74557&t=fjtAO3GqQycadNLQ-0
     * This element is for mobile to create custom formatted pre-show homepage banners for Markets
     */
    FAIRE_MARKETS_PRE_SHOW_BANNER = 'FAIRE_MARKETS_PRE_SHOW_BANNER',
    /**
     * https://www.figma.com/file/mVJQw4r8Oj2RMIDynzcigE/%E2%98%80%EF%B8%8F-Faire-Summer-Market-2023?type=design&node-id=3095-74557&t=fjtAO3GqQycadNLQ-0
     * This element is for mobile to create custom formatted hero homepage banners for Markets
     */
    FAIRE_MARKETS_HERO_BANNER = 'FAIRE_MARKETS_HERO_BANNER',
    /**
     * https://www.figma.com/file/wqHPF7FIpRnpkgc2HnZqil/Team%2C-permission%2C-and-collaborative-bag-education?type=design&node-id=692-446961&t=aOS73lKywD47N5WT-0
     * A banner used to educate retailer sub-users about their permissions and their team
     */
    RETAILER_MULTI_USER_TEAM_EDUCATION_BANNER = 'RETAILER_MULTI_USER_TEAM_EDUCATION_BANNER',
    /**
     * Used to display grids of various layout element data types, example: gift guide banner on holiday shop page
     * https://www.figma.com/file/acGGYdQ1JMiFe0KY1ZLgDG/Special-Occasions?node-id=4796%3A79865&mode=dev
     * @deprecated
     */
    DYNAMIC_GRID = 'DYNAMIC_GRID',
    /**
     * Used to display various marketing banners, example: Stock up for Holidays banner from homepage
     * https://www.figma.com/file/acGGYdQ1JMiFe0KY1ZLgDG/Special-Occasions?type=design&node-id=5476-90057&mode=dev
     */
    MARKETING_BANNER = 'MARKETING_BANNER',
    /**
     * Used to display the full width image header, example: holiday shop page image header
     * https://www.figma.com/file/acGGYdQ1JMiFe0KY1ZLgDG/Special-Occasions?type=design&node-id=5476-88749&mode=dev
     */
    FULL_WIDTH_IMAGE_BANNER = 'FULL_WIDTH_IMAGE_BANNER',
    /**
     * Used to display a callout for Spa & Salon retailers
     * https://www.figma.com/file/YHp4AyuAXlRWNoDFxhyP8x/Partial-Retailers---Design-Opps?node-id=462%3A68837&mode=dev
     * @deprecated
     */
    SPA_SALON_RETAILER_CALLOUT = 'SPA_SALON_RETAILER_CALLOUT',
    /**
     * Used to display an explanation of Faire's eco-friendly product attributes
     * https://www.figma.com/file/CQBNDWn5ukts0FRi9EvikO/Sustainability-page?type=design&node-id=1870-170092&mode=dev
     */
    ECO_FRIENDLY_DEFINITION_BANNER = 'ECO_FRIENDLY_DEFINITION_BANNER',
    /**
     * Used to display a small GTM promo banner on the Category Page
     */
    SMALL_DISMISSABLE_PROMO_BANNER = 'SMALL_DISMISSABLE_PROMO_BANNER',
    /**
     * Used to display Avatar Carousel (Logged out Visual Nav)
     * https://www.figma.com/file/gzAnBXkVaDKZZ1vYrm3iHm/2023-Q3-Quality-Issues-Support-SEO?type=design&node-id=268-20538&mode=design&t=SMhTvpuzKikfYO31-0
     */
    AVATAR_CAROUSEL = 'AVATAR_CAROUSEL',
    /**
     * Used to display Avatar Carousel And Avatar Grid on MWeb (Logged out Visual Nav)
     * https://www.figma.com/file/gzAnBXkVaDKZZ1vYrm3iHm/2023-Q3-Quality-Issues-Support-SEO?type=design&node-id=268-20538&mode=design&t=SMhTvpuzKikfYO31-0
     */
    AVATAR_CAROUSEL_WITH_GRID = 'AVATAR_CAROUSEL_WITH_GRID',
    /**
     * Used to display grids or carousel of various layout element data types, example: holiday shop page banners
     * https://www.figma.com/file/52DuPaecpzwgu59Z2ebvSZ/Special-Occasions---V2?type=design&node-id=5728-140790&mode=design&t=BesPO3nWWaYeKr6q-0
     */
    DYNAMIC_GRID_OR_CAROUSEL = 'DYNAMIC_GRID_OR_CAROUSEL',
    /**
     * Used to display the multi image header, example: holiday shop page image header
     * https://www.figma.com/file/52DuPaecpzwgu59Z2ebvSZ/Special-Occasions---V2?type=design&node-id=6152-67247&mode=design&t=7VfCpCf5hjQrueNy-0
     */
    MULTI_IMAGE_TITLE_BANNER = 'MULTI_IMAGE_TITLE_BANNER',
    /**
     * Used to display dynamic or alternating grids of various layout element data types, example: gift guide banner on holiday shop page
     * Web desktop currently uses dynamic grid while Mobile Web/App uses alternating grid
     * https://www.figma.com/file/52DuPaecpzwgu59Z2ebvSZ/Special-Occasions---V2?type=design&node-id=6152-67247&mode=design&t=N0mkOEMmpjK3YAck-0
     * @deprecated
     */
    DYNAMIC_OR_ALTERNATING_GRID = 'DYNAMIC_OR_ALTERNATING_GRID',
    /**
     * Used to display the banner for new brands with free shipping offer
     * https://www.figma.com/file/kWp8Y1nViYLSxAyUrx8EGi/Replace-insider-free-shipping-benefit-for-new-brands?type=design&node-id=4444%3A64181&mode=design&t=zizRLKAr5eISc1Ij-1
     */
    NEW_BRANDS_FREE_SHIPPING_BANNER = 'NEW_BRANDS_FREE_SHIPPING_BANNER',
    /**
     * Used to display the filterable carousel on the homepage
     * https://www.figma.com/file/XBKfeoIzmZdUqB0dyDeq1z/Homepage-strategy-2024?type=design&node-id=8271-44301&mode=design&t=qHvYJ5163O3HMujn-0
     * @deprecated
     */
    HOMEPAGE_FILTERABLE_CAROUSEL = 'HOMEPAGE_FILTERABLE_CAROUSEL',
    /**
     * Used to display dynamic and sparse grids of various layout element data types, example: gift guide banner on holiday shop page
     * https://www.figma.com/file/3I5Wiw0SQkHAN4zrDLpaWf/Tiered-browse-framework?type=design&node-id=828%3A58974&mode=dev&t=7eo9zPkXRFuJibXN-1
     */
    DYNAMIC_SPARSE_GRID = 'DYNAMIC_SPARSE_GRID',
    /**
     * Used by Browse Hub's homepage promotional banner. Similar to marketing banner, but it has different design on
     * mobile web vs desktop
     * https://www.figma.com/file/3I5Wiw0SQkHAN4zrDLpaWf/Tiered-browse-framework?type=design&node-id=1563-108318&mode=design&t=w5oPh7e7v3WPJt7N-4
     */
    BROWSE_HOMEPAGE_PROMOTIONAL_BANNER = 'BROWSE_HOMEPAGE_PROMOTIONAL_BANNER',
    /**
     * Used to display a horizontal line
     */
    HORIZONTAL_LINE = 'HORIZONTAL_LINE',
    /**
     * Used to display a single button
     */
    BUTTON = 'BUTTON',
    /**
     * A section containing a brand header and multiple product tiles, used for the digest page
     */
    DIGEST_PAGE_BRAND_SPOTLIGHT_SECTION = 'DIGEST_PAGE_BRAND_SPOTLIGHT_SECTION',
    /**
     * A section containing 3 images in a collage format, used for the digest page
     */
    IMAGE_COLLAGE = 'IMAGE_COLLAGE',
    /**
     * A breadcrumb list or placeholder for breadcrumb position.
     */
    BREADCRUMB = 'BREADCRUMB',
    /**
     * Grid for category tiles, with extra padding on the top. Example: browse landing page for you grid
     */
    CATEGORY_TILE_GRID = 'CATEGORY_TILE_GRID',
    /**
     * Displays a grid of components forming the header for the digest page
     */
    DIGEST_PAGE_HEADER_GRID = 'DIGEST_PAGE_HEADER_GRID',
    /**
     * Displays a grid of components forming the header for the digest page
     */
    DIGEST_PAGE_HEADER_BANNER = 'DIGEST_PAGE_HEADER_BANNER',
    /**
     * A brand header component on the digest page
     */
    DIGEST_PAGE_BRAND_HEADER = 'DIGEST_PAGE_BRAND_HEADER',
    /**
     * A banner for brand reviews on the home page
     * https://www.figma.com/design/huEPQLqODTdhFdQs67jBQy/Reviews-revamp?node-id=3734-11917&node-type=section&t=P12HgOl21XrFoTUi-0
     */
    BRAND_REVIEW_BANNER = 'BRAND_REVIEW_BANNER',
    /**
     * A client driven component for product reviews
     */
    PRODUCT_REVIEW_COMPONENT = 'PRODUCT_REVIEW_COMPONENT',
    /**
     * An identifier for the mobile app browse menu
     */
    MOBILE_APP_BROWSE_MENU = 'MOBILE_APP_BROWSE_MENU',
    /**
     * A header component for the brand mini shop on the product page
     */
    PRODUCT_PAGE_MINI_SHOP_HEADER = 'PRODUCT_PAGE_MINI_SHOP_HEADER',
    /**
     * Used to show similar products at the top of the logged out product page
     * https://www.figma.com/design/rknRLAsKcyA3FUOLlgQTuN/Product-pages-and-Google-Shopping?node-id=82-44954&p=f&t=rak0ueEVGiI6h2fJ-0
     */
    PRODUCT_PAGE_HEADER_CAROUSEL = 'PRODUCT_PAGE_HEADER_CAROUSEL',
    /**
     * Used for rendering the volume based discount carousel
     * https://www.figma.com/design/2gTnEHipC4km01di614K0P/Tiered-VBD?node-id=3408-30032&t=ECoruH2IqqhTP5Nc-4
     */
    VOLUME_BASED_DISCOUNT_CAROUSEL = 'VOLUME_BASED_DISCOUNT_CAROUSEL',
    /**
     * A carousel component that hosts 2 rows of [ImageFanTile]s
     * https://www.figma.com/design/0350Ly5gqCrvph3ng9Uei9/Visual-Nav-Whiteboard?node-id=2853-291436&t=hmT6LSBb1Jr2z71K-4
     */
    TWO_ROW_IMAGE_FAN_TILE_CAROUSEL = 'TWO_ROW_IMAGE_FAN_TILE_CAROUSEL',
  }

  /**
   * Where to display the layout element.
   */
  export enum DisplayPage {
    DISPLAY_PAGE_UNKNOWN = 'DISPLAY_PAGE_UNKNOWN',
    /**
     * @deprecated
     */
    LANDING_PAGE = 'LANDING_PAGE',
    /**
     * @deprecated
     */
    FLASH_SALE_PAGE = 'FLASH_SALE_PAGE',
    /**
     * This is only used directly in the home feed api. should be ignored by everyone else.
     */
    HOME_FEED = 'HOME_FEED',
    CATEGORY_PAGE = 'CATEGORY_PAGE',
    /**
     * HOMEPAGE will replace LANDING_PAGE if experiment succeeds
     */
    HOMEPAGE = 'HOMEPAGE',
    /**
     * Deprecated after new FAIRE_MARKET_HOMEPAGE_V2
     * @deprecated
     */
    FAIRE_MARKET_HOMEPAGE = 'FAIRE_MARKET_HOMEPAGE',
    SHOWROOM_PAGE = 'SHOWROOM_PAGE',
    /**
     * HOMEPAGE_FALLBACK will only be used while `IMPROVED_HOMEPAGE_RELEVANCE_220503` is launched,
     * to serve the old homepage to Mobile App. This will be deprecated when Mobile Homepage V2 enter 100% phase 1A.
     * More context: https://www.notion.so/faire/Homepage-Relevance-Mobile-Sync-9242a3ef0d574d34b71cc84dee52fa68#d8b306064d454ee89b46b4c27fc5c9bd
     * @deprecated
     */
    HOMEPAGE_FALLBACK = 'HOMEPAGE_FALLBACK',
    /**
     * FFW showtime takeover of the web homepage
     * @deprecated
     */
    FASHION_WEEK_HOMEPAGE_WEB = 'FASHION_WEEK_HOMEPAGE_WEB',
    /**
     * FFW showtime takeover of the app home feed
     * @deprecated
     */
    FASHION_WEEK_HOME_FEED_APP = 'FASHION_WEEK_HOME_FEED_APP',
    /**
     * Replaces HOMEPAGE during Markets showtime
     */
    FAIRE_MARKET_HOMEPAGE_V2 = 'FAIRE_MARKET_HOMEPAGE_V2',
    CART_PAGE = 'CART_PAGE',
    SUPPLIERS_PAGE = 'SUPPLIERS_PAGE',
    PRODUCT_PAGE = 'PRODUCT_PAGE',
    SIMILAR_PRODUCTS_PAGE = 'SIMILAR_PRODUCTS_PAGE',
    /**
     * The digest page is a regularly updated page dedicated for personalized and curated products
     */
    DIGEST_PAGE = 'DIGEST_PAGE',
    DISCOVER_PAGE = 'DISCOVER_PAGE',
    /**
     * Displays navigation links on mobile apps
     */
    BROWSE_LANDING_PAGE = 'BROWSE_LANDING_PAGE',
    BRAND_PAGE = 'BRAND_PAGE',
    LOGGED_OUT_HOMEPAGE = 'LOGGED_OUT_HOMEPAGE',
    LOGGED_OUT_CATEGORY_PAGE = 'LOGGED_OUT_CATEGORY_PAGE',
  }

  /**
   * Which platform to display the layout element.
   */
  export enum Platform {
    PLATFORM_UNKNOWN = 'PLATFORM_UNKNOWN',
    /**
     * web regardless of viewport
     */
    WEB = 'WEB',
    APP = 'APP',
    /**
     * mobile web only
     */
    WEB_MOBILE = 'WEB_MOBILE',
    /**
     * desktop web only
     */
    WEB_DESKTOP = 'WEB_DESKTOP',
  }

  export namespace ITileCta {
    export enum ActionType {
      ACTION_TYPE_UNKNOWN = 'ACTION_TYPE_UNKNOWN',
      /**
       * when ctaType == ActionType.DEFAULT, ctaValue should be "".
       * This is primarily for backward compatibility when ctaType is not recognized
       */
      DEFAULT = 'DEFAULT',
      /**
       * when ctaType == ActionType.APPLY_BRAND_CATEGORY, ctaValue should be one of the category
       */
      APPLY_BRAND_CATEGORY = 'APPLY_BRAND_CATEGORY',
    }

    /**
     * Return an instance of ITileCta with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<ITileCta>): ITileCta =>
      ({
        ...(partial ?? {}),
      }) as ITileCta;
  }

  export interface ITileCta {
    cta_value: string | undefined;
    cta_type: keyof typeof ILayoutElement.ITileCta.ActionType | undefined;
  }

  /**
   * Placement of layout elements on pages that have results (e.g. CATEGORY_PAGE)
   */
  export enum Placement {
    PLACEMENT_UNKNOWN = 'PLACEMENT_UNKNOWN',
    /**
     * @deprecated
     */
    TOP = 'TOP',
    INLINE = 'INLINE',
    BOTTOM = 'BOTTOM',
    ABOVE_TITLE = 'ABOVE_TITLE',
    BELOW_TITLE = 'BELOW_TITLE',
    BELOW_BREADCRUMB = 'BELOW_BREADCRUMB',
    TITLE_RIGHT = 'TITLE_RIGHT',
    MOBILE_ABOVE_DESCRIPTION = 'MOBILE_ABOVE_DESCRIPTION',
    MOBILE_TOP_FOOTER = 'MOBILE_TOP_FOOTER',
    MOBILE_BELOW_DESCRIPTION = 'MOBILE_BELOW_DESCRIPTION',
    ABOVE_FOOTER = 'ABOVE_FOOTER',
  }

  /**
   * Which page to display the layout element.
   */
  export enum PageVariant {
    PAGE_VARIANT_UNKNOWN = 'PAGE_VARIANT_UNKNOWN',
    /**
     * show layout element on all pages
     */
    ALL = 'ALL',
    /**
     * show layout element on first page
     */
    FIRST_PAGE = 'FIRST_PAGE',
    /**
     * show layout element on odd page number pages i.e.: 1, 3, 5 etc.
     */
    ODD = 'ODD',
    /**
     * show layout element on even page number pages i.e.: 2, 4, 6 etc.
     */
    EVEN = 'EVEN',
  }

  export namespace IMilestoneInteraction {
    export enum InteractionType {
      INTERACTION_TYPE_UNKNOWN = 'INTERACTION_TYPE_UNKNOWN',
      RECORD_UPON_DISMISS = 'RECORD_UPON_DISMISS',
      RECORD_UPON_SEEN = 'RECORD_UPON_SEEN',
      RECORD_UPON_CLICK = 'RECORD_UPON_CLICK',
    }

    /**
     * Return an instance of IMilestoneInteraction with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IMilestoneInteraction>
    ): IMilestoneInteraction =>
      ({
        ...(partial ?? {}),
      }) as IMilestoneInteraction;
  }

  export interface IMilestoneInteraction {
    user_milestone: IUserMilestone | undefined;
    type:
      | keyof typeof ILayoutElement.IMilestoneInteraction.InteractionType
      | undefined;
  }

  /**
   * Redacted copy of ILayoutElement (i.e. without redacted fields).
   */
  export type Redacted = Omit<
    ILayoutElement,
    'tokens' | 'impression_path_override'
  >;

  /**
   * Return an instance of ILayoutElement with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<ILayoutElement>): ILayoutElement =>
    ({
      images: [],
      tokens: [],
      layout_element_data: [],
      styles: {},
      milestone_interactions: [],
      badges: [],
      tile_badges_map: {},
      brands_by_token: {},
      layout_element_data_assign_settings_on_client_view: [],
      ...(partial ?? {}),
    }) as ILayoutElement;
}

export interface ILayoutElement {
  token: string | undefined;
  images: Array<IImage>;
  main_text: string | undefined;
  description_text: string | undefined;
  tagline_text: string | undefined;
  cta_text: string | undefined;
  /**
   * If this ctaLink refers to a category link, this will be its path identifier.
   * If it's a fake category (seasonal, featured, etc), the identifier is the category name.
   */
  category_path_identifier: string | undefined;
  type: keyof typeof ILayoutElement.Type | undefined;
  /**
   * The tokens upon which this layout element was built (if applicable). This field SHOULD NOT be used by the
   * frontend.
   * @protobufOption (indigofair.data.redacted) = true
   */
  tokens: Array<string>; // redacted
  /**
   * Pre-fetched for the tokens. The tokens can represent brands, flash sales, etc.
   * The tokens and this list are not guaranteed to match since we apply additional filtering to tokens before
   * returning them to guarantee we don't return restricted states, sold flash sales, etc.
   */
  layout_element_data: Array<ILayoutElementData>;
  priority: number | undefined;
  /**
   * This could either be the string literal "all", a retailer token or a retailer store type.
   * In the future, this could be even more complex, like California Gift Store, etc.
   * This field is deprecated and we will use identifier instead to be able to support different page layout elements.
   * @deprecated
   */
  retailer_identifier: string | undefined;
  /**
   * If clicked, the frontend SHOULD report a click event using this name. Like signals, this name is canonically
   * snake_case_and_descriptive.
   */
  on_click_event_name: string | undefined;
  tile_cta: ILayoutElement.ITileCta | undefined;
  /**
   * Not all clients will support custom colors for all types of elements. Use client code as the source of truth
   */
  background_color: string | undefined;
  /**
   * Not all clients will support custom colors for all types of elements. Use client code as the source of truth
   */
  cta_text_color: string | undefined;
  /**
   * Not all clients will support custom colors for all types of elements. Use client code as the source of truth
   */
  cta_text_background_color: string | undefined;
  /**
   * Not all clients will support custom colors for all types of elements. Use client code as the source of truth
   */
  main_text_color: string | undefined;
  /**
   * Not all clients will support custom colors for all types of elements. Use client code as the source of truth
   */
  description_text_color: string | undefined;
  /**
   * Not all clients will support custom colors for all types of elements. Use client code as the source of truth
   */
  tagline_text_color: string | undefined;
  display_page: keyof typeof ILayoutElement.DisplayPage | undefined;
  styles: Partial<Record<string, string>>;
  setting: string | undefined;
  /**
   * Paired with [setting], this is a serialized value that is checked against to utilize [setting]. For example, for
   * a [Boolean] setting, this is often `true`. For an enum setting `enum Foo { BAR, BAZ }`, this would be `"BAR"` or
   * `"BAZ"`
   */
  expected_json_serialized_setting_value: string | undefined;
  /**
   * When displayPage is a CATEGORY_PAGE, we need an identifier to tell which category page we are on.
   */
  category_page_identifier: string | undefined;
  /**
   * Formatted as ll-CC, or just ll if country is null.
   * For example, fr would be international French, and fr-FR would be French spoken in France.
   */
  locale: string | undefined;
  placement: keyof typeof ILayoutElement.Placement | undefined;
  /**
   * Currently on Mobile App, this is needed to record milestone for home feed tiles & other top banners
   * @deprecated
   */
  milestone_interaction: ILayoutElement.IMilestoneInteraction | undefined;
  /**
   * User milestones for clients to record *
   */
  milestone_interactions: Array<ILayoutElement.IMilestoneInteraction>;
  homepage_template_type:
    | keyof typeof IRetailerHomepageTemplate.Type
    | undefined;
  recommender_class_name: string | undefined;
  expires_at: number | undefined;
  title_icon: IImage | undefined;
  button_cta_text: string | undefined;
  /**
   * Badges on the LayoutElement, such as decorating an instance of a carousel with "New!"
   */
  badges: Array<IBadge>;
  /**
   * Badges on the individual tiles (LayoutElementData)
   */
  tile_badges_map: Partial<Record<string, IBadgeList>>;
  show_vertical_imagery: boolean | undefined;
  /**
   * Background image to use for cta, e.g. if the cta will appear as the last tile in a carousel
   */
  cta_background_image: IImage | undefined;
  /**
   * Background image to use for the LayoutElement
   */
  background_image: IImage | undefined;
  /**
   * Text used to display promotions related to the element.
   * Receives special formatting during promotional events on mobile.
   */
  promo_text: string | undefined;
  /**
   * Boolean to flag whether the promotional banner from promoText should be highlighted with event colours
   */
  is_highlighted_brand_for_promotional_event: boolean | undefined;
  /**
   * Button primarily used to toggle state of additional content.
   */
  toggle_button: ILayoutElement.IToggleButton | undefined;
  /**
   * LeanProductTile.Brand on the individual tiles (LayoutElementData)
   */
  brands_by_token: Partial<Record<string, ILeanProductTile.IBrand>>;
  platform: keyof typeof ILayoutElement.Platform | undefined;
  /**
   * This field holds a list of string names of settings
   * for clients to assign once the user scrolls past this LayoutElement, and it contains all layout
   * element data level experiment settings.
   * BE should populate LayoutElement.layoutElementData with all visible LayoutElementData;
   * Clients will record assignments for all settings in this field on the LayoutElement view.
   * This allows us to record assignments only when a user actually sees the diverging experience.
   * This temporary field/solution will be replaced by Server-driven UI (SDUI) eventually.
   */
  layout_element_data_assign_settings_on_client_view: Array<string>;
  /**
   * Inline CTA link that will appear in the description
   */
  description_cta_link: string | undefined;
  /**
   * Inline CTA Text that will appear in the description
   */
  description_cta_text: string | undefined;
  /**
   * Which page to display the layout element.
   */
  page_variant: keyof typeof ILayoutElement.PageVariant | undefined;
  /**
   * Deprecated. Use the impression id on the individual ElementLayoutData product tile (e.g. LeanProductTile)
   * @deprecated
   */
  impression_id: string | undefined;
  identifier: string | undefined;
  /**
   * This is an identifier that each recommender class will be responsible to fill in if the page has secondary identifier
   * (like category page where each category identifier will have different layout in the category page)
   */
  secondary_identifier: string | undefined;
  /**
   * @protobufOption (.faire.common.tokenType) = RETAILER
   */
  retailer_token: string | undefined;
  open_app_modal: ILayoutElement.IOpenModal | undefined;
  /**
   * Deprecated. This is only used to support legacy impression paths that do not conform to the standard impression path format.
   * @deprecated
   * @protobufOption (indigofair.data.redacted) = true
   */
  impression_path_override: string | undefined; // redacted
  cta_link: string | undefined;
  cta_link_to_search_makers: ISearchMakersWithFiltersRequest | undefined;
  cta_link_to_search_products: IProductFirstCatNavRequest | undefined;
}
